import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { api, Client, ApiError } from '@/lib/api';

export default function ClientsPage() {
  const [search, setSearch] = React.useState('');
  const [selectedClients, setSelectedClients] = React.useState<string[]>([]);
  const [clients, setClients] = React.useState<Client[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);

  const loadClients = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getClients();
      setClients(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao carregar clientes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este cliente? Esta acao nao pode ser desfeita e todas as plataformas associadas serao removidas.')) {
      return;
    }

    try {
      setIsDeleting(id);
      await api.deleteClient(id);
      setClients((prev) => prev.filter((c) => c.id !== id));
      setSelectedClients((prev) => prev.filter((clientId) => clientId !== id));
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir cliente');
    } finally {
      setIsDeleting(null);
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelectClient = (id: string) => {
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map((c) => c.id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-500 mt-1">
            Gerencie os clientes da plataforma
          </p>
        </div>
        <Link
          to="/clients/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25"
        >
          <Plus className="h-5 w-5" />
          Novo Cliente
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-12 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
            <p className="text-gray-500">Carregando clientes...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">Erro ao carregar clientes</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={loadClients}
              className="px-4 py-2 rounded-xl bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* Batch Actions */}
      {selectedClients.length > 0 && (
        <div className="bg-[#0057FF]/5 rounded-2xl border border-[#0057FF]/20 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#0057FF]">
              {selectedClients.length} cliente(s) selecionado(s)
            </span>
            <div className="flex gap-3">
              <button className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                Exportar
              </button>
              <button className="px-4 py-2 rounded-xl bg-red-500 text-sm font-medium text-white hover:bg-red-600 transition-colors inline-flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clients Table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="p-4 text-left">
                    <button
                      onClick={toggleSelectAll}
                      className="flex items-center justify-center"
                    >
                      <div
                        className={`h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                          selectedClients.length === filteredClients.length &&
                          filteredClients.length > 0
                            ? 'bg-[#0057FF] border-[#0057FF]'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {selectedClients.length === filteredClients.length &&
                          filteredClients.length > 0 && (
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                      </div>
                    </button>
                  </th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Plataformas
                  </th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="p-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Acoes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                          <Search className="h-8 w-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Nenhum cliente encontrado</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {search ? 'Tente buscar com outros termos' : 'Comece criando seu primeiro cliente'}
                          </p>
                        </div>
                        {!search && (
                          <Link
                            to="/clients/new"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0057FF]/90 transition-colors"
                          >
                            <Plus className="h-4 w-4" />
                            Novo Cliente
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client) => (
                    <tr
                      key={client.id}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="p-4">
                        <button
                          onClick={() => toggleSelectClient(client.id)}
                          className="flex items-center justify-center"
                        >
                          <div
                            className={`h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                              selectedClients.includes(client.id)
                                ? 'bg-[#0057FF] border-[#0057FF]'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {selectedClients.includes(client.id) && (
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center shadow-md">
                            <span className="text-white font-bold">
                              {client.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{client.name}</p>
                            <p className="text-xs text-gray-400">
                              Criado em {new Date(client.createdAt).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {client.tenants?.length || 0}
                          </span>
                          <span className="text-xs text-gray-500">
                            {client.tenants?.length === 1 ? 'plataforma' : 'plataformas'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div
                            className={`h-2.5 w-2.5 rounded-full ${
                              client.status === 'active'
                                ? 'bg-emerald-500'
                                : 'bg-gray-300'
                            }`}
                          />
                          <span className="text-sm text-gray-600">
                            {client.status === 'active' ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/clients/${client.id}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                          >
                            <Eye className="h-5 w-5" />
                          </Link>
                          <Link
                            to={`/clients/${client.id}/edit`}
                            className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                          >
                            <Pencil className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => handleDelete(client.id)}
                            disabled={isDeleting === client.id}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            {isDeleting === client.id ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredClients.length > 0 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Mostrando <span className="font-medium text-gray-900">{filteredClients.length}</span> de <span className="font-medium text-gray-900">{clients.length}</span> clientes
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
